import { ReviewsSharp } from "@mui/icons-material";
import { Box, Button, Card, Container, Divider, InputAdornment, TextField, Typography, useTheme } from "@mui/material";
import Logo from "components/Logo";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AccountContext } from "services/account";
import { ERROR_MESSAGE, getUserData, login, register } from "services/api";
import { checkEmail, isAllFalseValue, isAllTrueValue } from "services/helper";
import { showSnackbar } from "services/snackbarHelper";
const SignUp = () => {
	const theme = useTheme();
	const { t } = useTranslation(["common", "validation"]);
	const navigate = useNavigate();

	const account = React.useContext(AccountContext);

	const [formData, setFormData] = React.useState({ username: "", password: "", checkPassword: "", email: "", code: "" });
	const [errorTexts, setErrorTexts] = React.useState({ username: "", password: "", checkPassword: "", email: "", code: "" });

	const [isGetCode, setIsGetCode] = React.useState(false);
	const [sendCodeCount, setSendCodeCount] = React.useState(null);

	const [isLoginLoading, setIsLoginLoading] = React.useState(false);
	const [isSendLoading, setIsSendLoading] = React.useState(false);

	const enableSubmit = React.useMemo(() => {
		return isAllTrueValue(formData);
	}, [formData]);
	console.log(enableSubmit, isGetCode, isLoginLoading);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		if (name === "email") {
			setIsGetCode(false);
			setSendCodeCount(null);
			setFormData({ ...formData, [name]: value, code: "" });
		} else {
			setFormData({ ...formData, [name]: value });
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			setIsLoginLoading(true);
		}
	};

	const checkRequired = () => {
		let newErrorTexts = { username: "", password: "", checkPassword: "", email: "", code: "" };
		Object.keys(errorTexts).forEach((name: string | number | symbol) => {
			if (!isGetCode && name === "code") return;

			if (formData[name] === "") {
				newErrorTexts[name] = "Required";
			}
		});
		return newErrorTexts;
	};

	const showError = (resp) => {
		let newErrorTexts = { username: "", password: "", checkPassword: "", email: "", code: "" };
		console.log(resp.error);
		switch (resp.error) {
			case "Username, password, and email are required":
				newErrorTexts.username = "Required";
				newErrorTexts.password = "Required";
				newErrorTexts.email = "Required";
				setErrorTexts(newErrorTexts);
				break;
			case "Email already exists":
				newErrorTexts.email = resp.error;
				setErrorTexts(newErrorTexts);
				break;
			case "Invalid verification code":
				setErrorTexts({ ...errorTexts, code: "invalidCode" });
				break;
			case "Invalid email format":
				newErrorTexts.email = "invaildEmail";
				setErrorTexts(newErrorTexts);
				break;
			case "Failed to send vertification email":
				showSnackbar(t(resp.error, { ns: "validation" }), "error");
				break;
			default:
				showSnackbar(t(resp.error, { ns: "validation" }), "error");
		}
	};
	const sendCode = () => {
		if (isSendLoading) return;
		setIsSendLoading(true);
		const newErrorTexts = checkRequired();
		newErrorTexts.code = "";
		if (!checkEmail(formData.email)) {
			newErrorTexts.email = "invalidEmail";
		}
		setErrorTexts(newErrorTexts);
		if (!isAllFalseValue(newErrorTexts)) {
			setIsLoginLoading(false);
			return;
		}

		register(formData.username, formData.password, formData.email, "user", formData.code, true)
			.then((resp) => {
				if (resp.info === "Vertification code sent to email") {
					showSnackbar(t(resp.info, { ns: "validation" }), "success");
				}
				if (resp.info === "Verification code already sent. Please wait for 2 minutes before resending") {
					showSnackbar(t(resp.info, { ns: "validation" }), "error");
				}
				setSendCodeCount(120);
				setIsGetCode(true);

				setIsLoginLoading(false);
			})
			.catch((err) => {
				showError(err);
				setIsLoginLoading(false);
			})
			.finally(() => setIsSendLoading(false));
	};

	const handleLogin = () => {
		let newErrorTexts = checkRequired();

		if (formData.password && formData.checkPassword && formData.password !== formData.checkPassword) {
			newErrorTexts.checkPassword = "notEqualToPassword";
		}
		if (!checkEmail(formData.email)) {
			newErrorTexts.email = "invalidEmail";
		}
		setErrorTexts(newErrorTexts);
		if (isAllFalseValue(newErrorTexts)) {
			register(formData.username, formData.password, formData.email, "user", formData.code, false)
				.then((resp) => {
					if (resp.info === "Account created successfully") {
						showSnackbar(t(resp.info, { ns: "validation" }), "success");
						navigate("/app/signin");
					} else {
						showSnackbar(t(resp.info, { ns: "validation" }), "error");
					}
					setIsLoginLoading(false);
				})
				.catch((err) => {
					showError(err);
					setIsLoginLoading(false);
				});
		} else {
			setIsLoginLoading(false);
		}
	};
	React.useEffect(() => {
		if (isLoginLoading) handleLogin();
	}, [isLoginLoading]);
	React.useEffect(() => {
		if (sendCodeCount === null) return;
		if (sendCodeCount <= 0) {
			setSendCodeCount(null);
			return;
		}
		setTimeout(() => {
			setSendCodeCount((prev) => prev - 1);
		}, 1000);
	}, [sendCodeCount]);

	return (
		<Box
			minHeight="100vh"
			display="flex"
			justifyContent="center"
			alignItems="center"
			flexDirection="column">
			<Box
				m={2}
				mt="auto"></Box>
			<Container maxWidth="xs">
				<Card>
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						flexDirection="column"
						p={4}>
						<Box
							mb={2}
							display="flex"
							alignItems="center">
							<Logo />
							<Typography variant="h6">{t("signUp")}</Typography>
						</Box>
						<form
							onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
								event.preventDefault();
								setIsLoginLoading(true);
							}}>
							<TextField
								margin="normal"
								fullWidth
								name="username"
								label={t("username")}
								error={!!errorTexts.username}
								helperText={t(errorTexts.username, { ns: "validation" })}
								value={formData.username}
								onChange={handleChange}
								slotProps={{ htmlInput: { autoComplete: "username" } }}
							/>
							<TextField
								margin="normal"
								fullWidth
								name="password"
								type="password"
								label={t("password")}
								error={!!errorTexts.password}
								helperText={t(errorTexts.password, { ns: "validation" })}
								value={formData.password}
								onChange={handleChange}
							/>
							<TextField
								margin="normal"
								fullWidth
								name="checkPassword"
								type="password"
								label={t("confirmPassword")}
								error={!!errorTexts.checkPassword}
								helperText={t(errorTexts.checkPassword, { ns: "validation" })}
								value={formData.checkPassword}
								onChange={handleChange}
							/>
							<TextField
								margin="normal"
								fullWidth
								name="email"
								label={t("email")}
								error={!!errorTexts.email}
								helperText={t(errorTexts.email, { ns: "validation" })}
								value={formData.email}
								onChange={handleChange}
								slotProps={{
									input: {
										endAdornment: (
											<InputAdornment position="end">
												<Button
													disabled={sendCodeCount !== null || isSendLoading}
													size="small"
													variant="contained"
													onClick={sendCode}>
													{t("sendCode") + (sendCodeCount !== null ? `(${sendCodeCount})` : "")}
												</Button>
											</InputAdornment>
										),
									},
								}}
							/>
							{isGetCode && (
								<TextField
									margin="normal"
									fullWidth
									name="code"
									label={t("vertificationCode")}
									error={!!errorTexts.code}
									helperText={t(errorTexts.code, { ns: "validation" })}
									value={formData.code}
									onChange={handleChange}
								/>
							)}
							<Box
								width="100%"
								mt={2}
								mb={2}>
								<Button
									type="submit"
									disabled={isLoginLoading || !enableSubmit}
									fullWidth
									onClick={() => setIsLoginLoading(true)}
									variant="contained"
									color="primary">
									{t("signUp")}
								</Button>
							</Box>
						</form>
						<Box
							width="100%"
							mt={2}>
							<Divider />
						</Box>
						<Box my={2}>
							<Box
								component={"a"}
								href="/app/signin">
								{t("backSignIn")}
							</Box>
						</Box>
					</Box>
				</Card>
			</Container>
			{/* <Box my={2}>
				<LanguagePopper />
			</Box> */}
			<Box mt="auto">
				<Container maxWidth="xs">
					<Divider />
					<Box my={2}>
						<Typography
							color="text.primary"
							variant="body2">
							Copyright © {new Date().getFullYear()} MiExpert Limited.
						</Typography>
					</Box>
				</Container>
			</Box>
		</Box>
	);
};

export default SignUp;
